import { Task } from "@common/types/task";
import { useCallback, useMemo } from "react";
import { useTaskBlockingSection } from "./useTaskBlockingSection";
import { TaskBlockingList } from "./TaskBlockingList";
import { TaskBlockingListItem } from "./TaskBlockingListItem";

interface BlockedBySectionProps {
    task: Task;
    onBlockedByChange?: (blockedByTaskIds: string[]) => Promise<unknown>;
}

const BlockedByListItem = ({
    taskId,
    onNavigate,
    onRemove,
}: {
    taskId: string;
    onNavigate: (taskId: string) => void;
    onRemove?: (taskId: string) => void;
}) => (
    <TaskBlockingListItem
        taskId={taskId}
        onNavigate={onNavigate}
        onRemove={onRemove}
        displayMode="blockedBy"
    />
);

export const BlockedBySection = ({
    task,
    onBlockedByChange,
}: Readonly<BlockedBySectionProps>) => {
    const { taskSummaries, goToTask, addOpen, setAddOpen } =
        useTaskBlockingSection(task);

    const blockedByTaskIds = useMemo(
        () => task.blockedByTaskIds ?? [],
        [task.blockedByTaskIds],
    );
    const availableToAdd = useMemo(
        () =>
            taskSummaries.filter(
                (t) =>
                    t.id !== task.id &&
                    !blockedByTaskIds.includes(t.id) &&
                    !(task.blockingTaskIds ?? []).includes(t.id),
            ),
        [taskSummaries, task.id, blockedByTaskIds, task.blockingTaskIds],
    );

    const removeBlocker = useCallback(
        (id: string) => {
            onBlockedByChange?.(
                blockedByTaskIds.filter((taskId) => taskId !== id),
            );
        },
        [onBlockedByChange, blockedByTaskIds],
    );

    const addBlocker = useCallback(
        (taskId: string) => {
            onBlockedByChange?.([...blockedByTaskIds, taskId]);
        },
        [onBlockedByChange, blockedByTaskIds],
    );

    return (
        <TaskBlockingList
            taskIds={blockedByTaskIds}
            availableToAdd={availableToAdd}
            onAdd={addBlocker}
            onRemove={removeBlocker}
            addOpen={addOpen}
            setAddOpen={setAddOpen}
            goToTask={goToTask}
            emptyLabel="None"
            ItemComponent={BlockedByListItem}
        />
    );
};
